import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  X, Clock, Users, Flame, Beef, Wheat, Droplet, Plus, Check, Loader2,
  ListChecks, ChefHat, Sparkles, Inbox, Trash2, Star, Share2, TrendingUp, Upload, Globe2, Search,
  ShoppingCart, CheckSquare, Square,
} from 'lucide-react';
import { MEALS, todayStr } from './trackingConstants';
import { SmartShoppingListModal } from './SmartShoppingListModal';

const isNewRecipe = (createdAt) => {
  if (!createdAt) return false;
  return Date.now() - new Date(createdAt).getTime() < 48 * 3600 * 1000;
};

const PORTIONS = [
  { divisor: 1, fr: 'Portion entière', en: 'Full portion', pct: '100%' },
  { divisor: 2, fr: 'La moitié', en: 'Half', pct: '50%' },
  { divisor: 3, fr: 'Un tiers', en: 'A third', pct: '33%' },
];

const LogPortionModal = ({ recipe, en, api, onClose, onLogged }) => {
  const [divisor, setDivisor] = useState(1);
  const [meal, setMeal] = useState('lunch');
  const [date, setDate] = useState(todayStr());
  const [busy, setBusy] = useState(false);
  const m = recipe.macros;
  const scaled = { calories: m.calories / divisor, protein: m.protein / divisor, carbs: m.carbs / divisor, fat: m.fat / divisor };

  const confirm = async () => {
    setBusy(true);
    try {
      await api(`/api/personal-recipes/${recipe.id}/log`, { method: 'POST', body: JSON.stringify({ date, meal, portion_divisor: divisor }) });
      toast.success(en ? `${Math.round(scaled.calories)} kcal logged` : `${Math.round(scaled.calories)} kcal ajoutées à votre suivi`);
      onLogged?.(); onClose();
    } catch { toast.error(en ? 'Could not log this recipe' : "Impossible d'ajouter cette recette"); }
    finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[220] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="personal-recipe-log-modal">
      <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-md bg-zinc-900 border border-white/10 rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">{en ? 'Add to my tracking' : 'Ajouter à mon suivi'}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-zinc-400" data-testid="personal-recipe-log-close"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-zinc-400 mb-2">{en ? 'Portion eaten' : 'Portion consommée'}</p>
        <div className="grid grid-cols-3 gap-2 mb-5">
          {PORTIONS.map((p) => (
            <button key={p.divisor} onClick={() => setDivisor(p.divisor)}
              className={`rounded-xl border p-3 text-center transition-colors ${divisor === p.divisor ? 'bg-primary/20 border-primary text-white' : 'bg-zinc-800/60 border-white/10 text-zinc-400 hover:border-primary/40'}`}
              data-testid={`personal-portion-${p.divisor}`}>
              <p className="font-bold text-sm">{p.pct}</p>
              <p className="text-[10px] mt-0.5 leading-tight">{en ? p.en : p.fr}</p>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 text-xs text-zinc-400 mb-5 rounded-xl bg-zinc-800/50 border border-white/10 p-3">
          <span className="font-bold text-white text-sm">{Math.round(scaled.calories)} kcal</span>
          <span>P {Math.round(scaled.protein)}g</span><span>G {Math.round(scaled.carbs)}g</span><span>L {Math.round(scaled.fat)}g</span>
        </div>
        <p className="text-sm text-zinc-400 mb-2">{en ? 'Meal' : 'Repas'}</p>
        <div className="grid grid-cols-3 gap-2 mb-5">
          {MEALS.map((mm) => (
            <button key={mm.key} onClick={() => setMeal(mm.key)}
              className={`flex items-center justify-center gap-1.5 rounded-xl border py-2.5 text-xs font-semibold transition-colors ${meal === mm.key ? 'bg-primary/20 border-primary text-white' : 'bg-zinc-800/60 border-white/10 text-zinc-400 hover:border-primary/40'}`}
              data-testid={`personal-meal-pick-${mm.key}`}>
              <mm.icon className="w-3.5 h-3.5" /> {en ? mm.en : mm.fr}
            </button>
          ))}
        </div>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="w-full bg-zinc-800 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:border-primary focus:outline-none mb-5"
          data-testid="personal-recipe-log-date" />
        <button onClick={confirm} disabled={busy}
          className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.01] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
          data-testid="personal-recipe-log-confirm">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {en ? 'Confirm' : 'Confirmer'}
        </button>
      </motion.div>
    </motion.div>
  );
};

const RecipeDetail = ({ recipe, en, api, onClose, onLogged, onDelete, onToggleFavorite, onShare }) => {
  const [logging, setLogging] = useState(false);
  const [sharing, setSharing] = useState(false);
  const m = recipe.macros;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[210] bg-black/85 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-testid="personal-recipe-detail-modal">
      <div className="min-h-full flex items-end sm:items-center justify-center p-0 sm:p-4">
        <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} onClick={(e) => e.stopPropagation()}
          className="relative w-full sm:max-w-xl bg-zinc-900 border border-white/10 rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[92vh] flex flex-col">
          <div className="relative h-56 sm:h-64 shrink-0 bg-zinc-800">
            {recipe.image_url && <img src={recipe.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />}
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-black/30 to-black/40" />
            <button onClick={onClose} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80 transition-colors" data-testid="personal-recipe-detail-close"><X className="w-5 h-5" /></button>
            <button onClick={() => onDelete(recipe.id)} className="absolute top-3 left-3 w-9 h-9 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-red-500/70 transition-colors" data-testid="personal-recipe-delete-btn"><Trash2 className="w-4 h-4" /></button>
            <button onClick={() => onToggleFavorite(recipe.id)} className="absolute top-3 left-14 w-9 h-9 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-yellow-500/70 transition-colors" data-testid="personal-recipe-favorite-btn">
              <Star className={`w-4 h-4 ${recipe.favorite ? 'fill-yellow-400 text-yellow-400' : ''}`} />
            </button>
            <div className="absolute bottom-4 left-4 right-4">
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary/80 text-white"><Sparkles className="w-2.5 h-2.5" /> {en ? 'From your meal plan' : 'Issue de votre plan alimentaire'}</span>
                {isNewRecipe(recipe.created_at) && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-yellow-400 text-black" data-testid="personal-recipe-new-badge">{en ? 'New' : 'Nouveau'}</span>
                )}
              </div>
              <h3 className="text-white text-xl sm:text-2xl font-black leading-tight">{recipe.name}</h3>
              <div className="flex items-center gap-3 mt-2 text-xs text-zinc-300">
                <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {recipe.prep_min} min</span>
                <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {recipe.servings}</span>
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-5 sm:p-6">
            <div className="grid grid-cols-4 gap-2 mb-6">
              {[['calories', Flame, m.calories, 'text-primary'], ['protein', Beef, m.protein, 'text-pink-400'], ['carbs', Wheat, m.carbs, 'text-yellow-400'], ['fat', Droplet, m.fat, 'text-emerald-400']].map(([k, Icon, val, color]) => (
                <div key={k} className="rounded-xl bg-zinc-800/60 border border-white/10 p-2.5 text-center">
                  <Icon className={`w-4 h-4 mx-auto mb-1 ${color}`} />
                  <p className="font-bold text-white text-sm">{Math.round(val)}{k === 'calories' ? '' : 'g'}</p>
                  <p className="text-[10px] text-zinc-500">{k === 'calories' ? 'kcal' : ''}</p>
                </div>
              ))}
            </div>
            {recipe.ingredients?.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-1.5"><ListChecks className="w-4 h-4 text-primary" /> {en ? 'Ingredients' : 'Ingrédients'}</h4>
                <ul className="space-y-1.5" data-testid="personal-recipe-ingredients">
                  {recipe.ingredients.map((ing, i) => <li key={i} className="text-sm text-zinc-300 border-b border-white/5 pb-1.5">{ing}</li>)}
                </ul>
              </div>
            )}
            {recipe.steps?.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-1.5"><ChefHat className="w-4 h-4 text-primary" /> {en ? 'Steps' : 'Préparation'}</h4>
                <ol className="space-y-3" data-testid="personal-recipe-steps">
                  {recipe.steps.map((s, i) => (
                    <li key={i} className="flex gap-3 text-sm text-zinc-300">
                      <span className="shrink-0 w-6 h-6 rounded-full bg-primary/20 border border-primary/40 text-primary text-xs font-bold flex items-center justify-center">{i + 1}</span>
                      <span className="pt-0.5">{s}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
          <div className="p-4 sm:p-5 border-t border-white/10 shrink-0 flex gap-2">
            <button onClick={() => setLogging(true)} className="flex-1 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.01] transition-transform flex items-center justify-center gap-2" data-testid="personal-recipe-add-to-tracking-btn">
              <Plus className="w-4 h-4" /> {en ? 'Add to my tracking' : 'Ajouter à mon suivi'}
            </button>
            <button onClick={async () => { setSharing(true); await onShare(recipe.id); setSharing(false); }} disabled={sharing}
              className="shrink-0 w-12 rounded-xl border border-white/10 text-zinc-300 hover:border-primary/50 hover:text-primary transition-colors flex items-center justify-center disabled:opacity-60"
              data-testid="personal-recipe-share-btn" title={en ? 'Share' : 'Partager'}>
              {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            </button>
          </div>
        </motion.div>
      </div>
      <AnimatePresence>
        {logging && <LogPortionModal recipe={recipe} en={en} api={api} onClose={() => setLogging(false)} onLogged={() => { onLogged?.(); onClose(); }} />}
      </AnimatePresence>
    </motion.div>
  );
};

const ImportRecipeModal = ({ en, api, onClose, onImported }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (text.trim().length < 20) {
      toast.error(en ? 'Paste a more complete recipe (ingredients + steps)' : 'Collez une recette plus complète (ingrédients + étapes)');
      return;
    }
    setBusy(true);
    try {
      await api('/api/personal-recipes/import', { method: 'POST', body: JSON.stringify({ text }) });
      toast.success(en ? 'Recipe imported with calculated macros!' : 'Recette importée avec macros calculées !');
      onImported(); onClose();
    } catch { toast.error(en ? "Could not analyze this recipe, try rephrasing it" : "Impossible d'analyser cette recette, essayez de la reformuler"); }
    finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[220] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="import-recipe-modal">
      <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-lg bg-zinc-900 border border-white/10 rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><Upload className="w-5 h-5 text-primary" /> {en ? 'Import a recipe' : 'Importer une recette'}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-zinc-400" data-testid="import-recipe-close"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-zinc-400 mb-3">{en ? 'Paste any recipe (ingredients + steps) — our AI structures it and calculates the macros per portion automatically.' : "Collez n'importe quelle recette (ingrédients + étapes) — l'IA la structure et calcule les macros par portion automatiquement."}</p>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={10}
          placeholder={en ? 'Chicken Rice Bowl\n2 servings\n- 300g chicken breast\n- 150g rice\n...\n1. Cook the rice...' : 'Poulet Riz Brocolis\n2 portions\n- 300g de blanc de poulet\n- 150g de riz\n...\n1. Cuire le riz...'}
          className="w-full bg-zinc-800 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:border-primary focus:outline-none mb-4 resize-none"
          data-testid="import-recipe-textarea" />
        <button onClick={submit} disabled={busy}
          className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.01] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
          data-testid="import-recipe-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {busy ? (en ? 'Analyzing...' : 'Analyse en cours...') : (en ? 'Analyze & import' : 'Analyser et importer')}
        </button>
      </motion.div>
    </motion.div>
  );
};

export const PersonalRecipesTab = ({ api, en }) => {
  const [recipes, setRecipes] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [topMonth, setTopMonth] = useState(null);
  const [importing, setImporting] = useState(false);
  const [query, setQuery] = useState('');
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [shoppingListOpen, setShoppingListOpen] = useState(false);

  const load = () => api('/api/personal-recipes').then((d) => setRecipes(d.recipes || [])).catch(() => setRecipes([]));
  useEffect(() => {
    load();
    api('/api/personal-recipes/stats').then((d) => setTopMonth(d.recipes || [])).catch(() => setTopMonth([]));
  }, []); // eslint-disable-line

  const filteredRecipes = useMemo(() => {
    if (!recipes) return recipes;
    const q = query.trim().toLowerCase();
    if (!q) return recipes;
    return recipes.filter((r) => r.name.toLowerCase().includes(q));
  }, [recipes, query]);

  const handleDelete = async (id) => {
    if (!window.confirm(en ? 'Delete this recipe?' : 'Supprimer cette recette ?')) return;
    try {
      await api(`/api/personal-recipes/${id}`, { method: 'DELETE' });
      setOpenId(null);
      toast.success(en ? 'Recipe deleted' : 'Recette supprimée');
      load();
    } catch { toast.error(en ? 'Could not delete' : 'Impossible de supprimer'); }
  };

  const opened = recipes?.find((r) => r.id === openId);

  const toggleSelect = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const selectedIngredients = (recipes || []).filter((r) => selectedIds.has(r.id)).flatMap((r) => r.ingredients || []);

  const handleToggleFavorite = async (id) => {
    setRecipes((prev) => prev.map((r) => (r.id === id ? { ...r, favorite: !r.favorite } : r)));
    try {
      await api(`/api/personal-recipes/${id}/favorite`, { method: 'POST' });
    } catch { toast.error(en ? 'Could not update favorite' : 'Impossible de mettre à jour le favori'); load(); }
  };

  const handleShare = async (id) => {
    try {
      const d = await api(`/api/personal-recipes/${id}/share`, { method: 'POST' });
      const url = `${window.location.origin}/recette-partagee/${d.share_token}`;
      if (navigator.share) {
        await navigator.share({ title: 'Body-Up — Recette', url }).catch(() => {});
      } else {
        await navigator.clipboard.writeText(url);
        toast.success(en ? 'Link copied to clipboard!' : 'Lien copié dans le presse-papiers !');
      }
    } catch { toast.error(en ? 'Could not share this recipe' : 'Impossible de partager cette recette'); }
  };

  return (
    <div data-testid="personal-recipes-tab">
      <div className="flex items-start justify-between gap-3 mb-4">
        <p className="text-sm text-zinc-400">{en ? 'Recipes automatically saved from your generated meal plans — log any of them straight to your daily tracking.' : 'Recettes sauvegardées automatiquement depuis vos plans alimentaires générés — ajoutez-les directement à votre suivi du jour.'}</p>
        <div className="shrink-0 flex items-center gap-2">
          <button
            onClick={() => { setSelectionMode((v) => !v); setSelectedIds(new Set()); }}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border transition-colors ${selectionMode ? 'bg-primary border-primary text-white' : 'border-white/10 text-zinc-300 hover:border-primary/50 hover:text-primary'}`}
            data-testid="personal-recipes-selection-toggle-btn"
          >
            <ShoppingCart className="w-3.5 h-3.5" /> {selectionMode ? (en ? 'Cancel' : 'Annuler') : (en ? 'Shopping list' : 'Liste de courses')}
          </button>
          <button onClick={() => setImporting(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border border-white/10 text-zinc-300 hover:border-primary/50 hover:text-primary transition-colors" data-testid="import-recipe-btn">
            <Upload className="w-3.5 h-3.5" /> {en ? 'Import' : 'Importer'}
          </button>
        </div>
      </div>
      {selectionMode && (
        <div className="sticky top-2 z-10 mb-4 flex items-center justify-between gap-3 rounded-2xl bg-primary/15 border border-primary/40 px-4 py-2.5" data-testid="personal-recipes-selection-bar">
          <p className="text-sm text-white font-semibold">{en ? `${selectedIds.size} recipe(s) selected` : `${selectedIds.size} recette(s) sélectionnée(s)`}</p>
          <button
            onClick={() => setShoppingListOpen(true)}
            disabled={selectedIds.size === 0}
            className="px-4 py-1.5 rounded-full text-xs font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-40"
            data-testid="personal-recipes-generate-list-btn"
          >
            {en ? 'Generate list' : 'Générer la liste'}
          </button>
        </div>
      )}
      <a href="/recettes-communaute" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-primary transition-colors mb-4" data-testid="community-gallery-link">
        <Globe2 className="w-3.5 h-3.5" /> {en ? 'Browse the community gallery' : 'Parcourir la galerie communauté'}
      </a>
      {recipes && recipes.length > 0 && (
        <div className="relative mb-4">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder={en ? 'Search a personal recipe…' : 'Rechercher une recette personnelle…'}
            className="w-full pl-9 pr-3 py-2.5 rounded-full text-sm bg-white/[0.035] backdrop-blur-xl border border-white/10 text-white placeholder:text-zinc-500 focus:outline-none focus:border-primary/50"
            data-testid="personal-recipes-search-input"
          />
        </div>
      )}
      {topMonth && topMonth.length > 0 && (
        <div className="mb-5 rounded-2xl border border-yellow-500/20 bg-yellow-500/5 p-3.5" data-testid="personal-recipes-top-month">
          <p className="text-xs font-bold text-yellow-300 mb-2.5 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> {en ? 'Your top recipes this month' : 'Vos recettes les plus utilisées ce mois'}</p>
          <div className="flex gap-2 overflow-x-auto">
            {topMonth.map((r) => (
              <button key={r.id} onClick={() => setOpenId(r.id)} className="shrink-0 flex items-center gap-2 bg-zinc-900/60 border border-white/10 rounded-xl px-3 py-2 hover:border-yellow-400/40 transition-colors" data-testid={`top-month-recipe-${r.id}`}>
                <span className="text-xs font-bold text-white truncate max-w-[140px]">{r.name}</span>
                <span className="text-[10px] text-yellow-300 font-bold shrink-0">×{r.log_count_month}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {recipes === null && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}
      {recipes && recipes.length === 0 && (
        <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-10 text-center" data-testid="personal-recipes-empty">
          <Inbox className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">{en ? 'No personal recipes yet. Generate a meal plan to fill this space.' : "Aucune recette personnelle pour l'instant. Générez un plan alimentaire pour remplir cet espace."}</p>
        </div>
      )}
      {recipes && recipes.length > 0 && filteredRecipes.length === 0 && (
        <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-10 text-center" data-testid="personal-recipes-search-empty">
          <Search className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">{en ? 'No recipe matches your search.' : 'Aucune recette ne correspond à votre recherche.'}</p>
        </div>
      )}
      {filteredRecipes && filteredRecipes.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {filteredRecipes.map((r) => {
            const isSelected = selectedIds.has(r.id);
            return (
              <motion.div key={r.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                onClick={() => (selectionMode ? toggleSelect(r.id) : setOpenId(r.id))}
                role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') (selectionMode ? toggleSelect(r.id) : setOpenId(r.id)); }}
                className={`text-left rounded-2xl overflow-hidden border bg-zinc-900 aspect-[4/5] transition-colors relative cursor-pointer ${isSelected ? 'border-primary' : 'border-white/10 hover:border-primary/50'}`}
                data-testid={`personal-recipe-card-${r.id}`}>
                {r.image_url ? <img src={r.image_url} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 flex items-center justify-center bg-zinc-800"><ChefHat className="w-6 h-6 text-zinc-600" /></div>}
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
                {selectionMode ? (
                  <div className="absolute top-2 right-2" data-testid={`personal-recipe-select-check-${r.id}`}>
                    {isSelected ? <CheckSquare className="w-6 h-6 text-primary bg-black/70 rounded" /> : <Square className="w-6 h-6 text-white bg-black/70 rounded" />}
                  </div>
                ) : (
                  <button onClick={(e) => { e.stopPropagation(); handleToggleFavorite(r.id); }}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center hover:bg-yellow-500/70 transition-colors"
                    data-testid={`personal-recipe-favorite-toggle-${r.id}`}>
                    <Star className={`w-3.5 h-3.5 ${r.favorite ? 'fill-yellow-400 text-yellow-400' : 'text-white'}`} />
                  </button>
                )}
                {isNewRecipe(r.created_at) && !selectionMode && (
                  <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-yellow-400 text-black" data-testid={`personal-recipe-new-badge-${r.id}`}>{en ? 'New' : 'Nouveau'}</span>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-2.5">
                  <p className="text-white text-xs font-bold line-clamp-2 leading-snug">{r.name}</p>
                  <p className="text-[10px] text-zinc-400 mt-1">{Math.round(r.macros.calories)} kcal</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
      <AnimatePresence>
        {opened && <RecipeDetail recipe={opened} en={en} api={api} onClose={() => setOpenId(null)} onLogged={load} onDelete={handleDelete} onToggleFavorite={handleToggleFavorite} onShare={handleShare} />}
        {importing && <ImportRecipeModal en={en} api={api} onClose={() => setImporting(false)} onImported={load} />}
        {shoppingListOpen && (
          <SmartShoppingListModal
            rawIngredients={selectedIngredients}
            sourceLabel={en ? `${selectedIds.size} selected recipe(s)` : `${selectedIds.size} recette(s) sélectionnée(s)`}
            en={en} onClose={() => setShoppingListOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default PersonalRecipesTab;
